// Global search widget — centered modal overlay (Linear / Raycast pattern).
//
// Wired by the top-bar input in Normal.js: clicking the input (or pressing
// ⌘K / Ctrl+K from anywhere) opens this modal. The top-bar input itself is
// presentational — the actual input that fields keystrokes lives inside the
// modal so it can grab focus reliably and survive the popover's mount cycle.
//
// Behaviour:
//   * 250 ms debounce on keystrokes, AbortController cancels in-flight.
//   * Empty input shows RECENT (localStorage, last 5) + SUGGESTIONS (static).
//   * Typed input shows results from /api/user/v1/search grouped by category
//     with a small category label on the right of each row.
//   * Up/Down arrows move selection across the flat row list. Enter opens the
//     selected row. Esc closes.
//
// This is a small UI consumed by Normal.js as a controlled component:
//   <GlobalSearchModal open={open} onClose={...} />

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Stack, Typography, Modal, IconButton, CircularProgress, InputBase,
} from "@mui/material";
import {
  Search as SearchIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  Language as SiteIcon,
  Folder as ProjectIcon,
  AccountTree as ZoneIcon,
  Block as FwIcon,
  Speed as RlIcon,
  Settings as ExceptionIcon,
  Code as ApiIcon,
  NotificationsActive as AlertIcon,
  Notifications as AlertEventIcon,
  WarningAmber as FindingIcon,
  Article as AuditIcon,
  Description as PageIcon,
  Bolt as BoltIcon,
  Shield as ShieldIcon,
  AddCircleOutline as SuggestionAddIcon,
  TuneOutlined as SuggestionTuneIcon,
  VpnKeyOutlined as SuggestionKeyIcon,
} from "@mui/icons-material";

import axios from "../../utils/axios/v1/userAxios";
import colors from "../../utils/colors";
import { filterSafeText } from "../../utils/validation/safeText";

const brandBlue = colors.primary[300];
const DEBOUNCE_MS = 250;
const MIN_Q_LEN = 2;
/** Fixed modal shell — body scrolls inside; avoids height jumping with result count. */
const MODAL_WIDTH = 640;
const MODAL_HEIGHT = 480;
const RECENT_KEY = "layerd_recent_searches";
const RECENT_MAX = 5;

// Icon + short label per category id — the label is used as the right-aligned
// badge text for each result row (matches the screenshot's "Site · under
// attack" / "WAF custom rule" style).
const CAT_META = {
  navigation:       { Icon: PageIcon,       label: "Page" },
  sites:            { Icon: SiteIcon,       label: "Site" },
  projects:         { Icon: ProjectIcon,    label: "Project" },
  zones:            { Icon: ZoneIcon,       label: "Zone" },
  firewall_rules:   { Icon: FwIcon,         label: "Firewall rule" },
  rate_limit_rules: { Icon: RlIcon,         label: "Rate-limit rule" },
  waf_exceptions:   { Icon: ExceptionIcon,  label: "WAF exception" },
  bot_exceptions:   { Icon: ExceptionIcon,  label: "Bot exception" },
  custom_rules:     { Icon: ShieldIcon,     label: "WAF custom rule" },
  crs_rules:        { Icon: ShieldIcon,     label: "CRS rule" },
  alert_rules:      { Icon: AlertIcon,      label: "Alert rule" },
  alert_events:     { Icon: AlertEventIcon, label: "Alert event" },
  api_endpoints:    { Icon: ApiIcon,        label: "API endpoint" },
  waf_events:       { Icon: BoltIcon,       label: "WAF event" },
  bm_events:        { Icon: BoltIcon,       label: "Bot event" },
  rl_events:        { Icon: BoltIcon,       label: "Rate-limit event" },
  api_events:       { Icon: BoltIcon,       label: "API event" },
  audit_logs:       { Icon: AuditIcon,      label: "Audit log" },
  scan_findings:    { Icon: FindingIcon,    label: "Scan finding" },
};

// Static contextual suggestions shown alongside RECENT in the empty state.
// These mirror the screenshot's "Add subdomain / Bot challenge for /login /
// Rotate API key" pattern — quick actions the user is likely to want.
const SUGGESTIONS = [
  { id: "sug_add_site",   Icon: SuggestionAddIcon,  title: "Add subdomain",          badge: "Action",         path: "/v2/sites/new" },
  { id: "sug_new_alert",  Icon: SuggestionTuneIcon, title: "Create alert rule",      badge: "Recommended",    path: "/v2/settings/alerts" },
  { id: "sug_audit",      Icon: AuditIcon,          title: "View audit log",         badge: "Compliance",     path: "/v2/settings/audit-logs" },
  { id: "sug_alerts_test",Icon: SuggestionKeyIcon,  title: "Test fire an alert",     badge: "Verification",   path: "/v2/settings/alerts" },
];

function loadRecent() {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.slice(0, RECENT_MAX) : [];
  } catch (_) { return []; }
}

function pushRecent(q) {
  if (!q) return;
  try {
    const cur = loadRecent().filter((x) => x !== q);
    cur.unshift(q);
    localStorage.setItem(RECENT_KEY, JSON.stringify(cur.slice(0, RECENT_MAX)));
  } catch (_) { /* quota — ignore */ }
}

function highlight(text, q) {
  if (!text || !q) return text || "";
  const s = String(text);
  const i = s.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return s;
  return (
    <>
      {s.slice(0, i)}
      <Box component="span" sx={{
        backgroundColor: "#FFF2A8", color: colors.neutral[1000],
        borderRadius: "2px", padding: "0 1px",
      }}>{s.slice(i, i + q.length)}</Box>
      {s.slice(i + q.length)}
    </>
  );
}

// Flatten {categories:[{items:[]}, ...]} into a single ordered row array
// so arrow-key navigation has a stable index across all visible rows.
// Each output row: {kind:"item"|"recent"|"suggest", payload, ...}
function flattenResults(payload) {
  if (!payload || !Array.isArray(payload.categories)) return [];
  const rows = [];
  for (const cat of payload.categories) {
    for (const it of cat.items || []) {
      rows.push({ kind: "item", catId: cat.id, item: it });
    }
  }
  return rows;
}

// ─────────────────────────────────────────────────────────────────────
// Modal entry — opened by parent (Normal.js)
// ─────────────────────────────────────────────────────────────────────
export default function GlobalSearchModal({ open, onClose }) {
  const navigate = useNavigate();
  const abortRef = useRef(null);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);
  const [err, setErr] = useState(null);
  const [recent, setRecent] = useState(loadRecent);
  const [sel, setSel] = useState(0);

  // Reset on open/close so the modal always feels fresh.
  useEffect(() => {
    if (open) {
      setQ("");
      setPayload(null);
      setErr(null);
      setSel(0);
      setRecent(loadRecent());
      // Defer focus so the Modal has time to mount the input.
      const t = setTimeout(() => inputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    } else {
      if (abortRef.current) abortRef.current.abort();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    }
  }, [open]);

  // Debounced fetch.
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.trim().length < MIN_Q_LEN) {
      setPayload(null); setLoading(false); setErr(null);
      if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; }
      return;
    }
    debounceRef.current = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setLoading(true); setErr(null);
      try {
        const r = await axios.get("search", {
          params: { q: q.trim(), limit: 5 },
          signal: ctrl.signal,
        });
        setPayload(r.data);
        setSel(0);
      } catch (e) {
        if (e.name !== "CanceledError" && e.code !== "ERR_CANCELED") {
          setErr(e.response?.data?.message || e.message || "Search failed");
          setPayload(null);
        }
      } finally { setLoading(false); }
    }, DEBOUNCE_MS);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [q, open]);

  // ── Row list — depends on whether the user has typed a query
  // ─────────────────────────────────────────────────────────────
  const rows = useMemo(() => {
    if (q.trim().length === 0) {
      // Empty state: recents + suggestions, ordered for keyboard nav.
      return [
        ...recent.map((r) => ({ kind: "recent", q: r })),
        ...SUGGESTIONS.map((s) => ({ kind: "suggest", payload: s })),
      ];
    }
    if (q.trim().length < MIN_Q_LEN) return [];
    return flattenResults(payload);
  }, [q, recent, payload]);

  // Clamp selection so it stays in bounds when rows change.
  useEffect(() => { setSel((s) => Math.min(Math.max(0, s), Math.max(0, rows.length - 1))); }, [rows]);

  const onPick = useCallback((row) => {
    if (!row) return;
    if (row.kind === "recent") {
      setQ(row.q);
      inputRef.current?.focus();
      return;
    }
    if (row.kind === "suggest") {
      navigate(row.payload.path);
      onClose();
      return;
    }
    if (row.kind === "item") {
      pushRecent(q.trim());
      setRecent(loadRecent());
      if (row.item.path) navigate(row.item.path);
      onClose();
    }
  }, [navigate, onClose, q]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(rows.length - 1, s + 1)); }
    else if (e.key === "ArrowUp")  { e.preventDefault(); setSel((s) => Math.max(0, s - 1)); }
    else if (e.key === "Enter")    { e.preventDefault(); if (rows[sel]) onPick(rows[sel]); }
    else if (e.key === "Escape")   { e.preventDefault(); onClose(); }
  };

  // Group typed results by category for the section-header rendering.
  const typedCategories = q.trim().length >= MIN_Q_LEN ? (payload?.categories || []) : null;
  let flatIdxCursor = 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        paddingTop: "12vh",
      }}
      slotProps={{
        backdrop: { sx: { backgroundColor: "rgba(17, 17, 17, 0.45)" } },
      }}
    >
      <Box
        onKeyDown={onKeyDown}
        sx={{
          width: MODAL_WIDTH,
          maxWidth: "92vw",
          height: MODAL_HEIGHT,
          maxHeight: "85vh",
          backgroundColor: "#fff",
          borderRadius: "14px",
          boxShadow: "0 24px 60px rgba(0,0,0,0.22), 0 2px 8px rgba(0,0,0,0.12)",
          border: "1px solid " + colors.neutral[200],
          display: "flex",
          flexDirection: "column",
          outline: "none",
          overflow: "hidden",
        }}
        tabIndex={-1}
      >
        {/* Input row — single source of truth for typed queries */}
        <Stack direction="row" alignItems="center" spacing={1.5}
          sx={{
            flexShrink: 0,
            px: 2.5, py: 1.75,
            borderBottom: "1px solid " + colors.neutral[200],
          }}>
          <SearchIcon sx={{ fontSize: 18, color: colors.neutral[600] }} />
          <InputBase
            inputRef={inputRef}
            value={q}
            onChange={(e) => setQ(filterSafeText(e.target.value))}
            placeholder="Search domains, rules, certs, IPs, paths…"
            sx={{
              flex: 1, fontSize: 14, color: colors.neutral[1000],
              "& input::placeholder": { color: colors.neutral[500], opacity: 1 },
            }}
            autoFocus
          />
          {loading ? (
            <CircularProgress size={14} sx={{ color: colors.neutral[500] }} />
          ) : q ? (
            <IconButton size="small" onClick={() => { setQ(""); inputRef.current?.focus(); }}>
              <CloseIcon sx={{ fontSize: 14, color: colors.neutral[500] }} />
            </IconButton>
          ) : null}
          <Box sx={{
            fontFamily: "monospace", fontSize: 11, color: colors.neutral[600],
            border: "1px solid " + colors.neutral[300], borderRadius: "4px",
            padding: "1px 7px", backgroundColor: colors.neutral[50],
          }}>esc</Box>
        </Stack>

        {/* Body */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {/* Empty state — recents + suggestions */}
          {q.trim().length === 0 && (
            <>
              <SectionLabel>Recent</SectionLabel>
              {recent.length === 0 ? (
                <Box sx={{ px: 2.5, pb: 1.5 }}>
                  <Typography sx={{ fontSize: 12.5, color: colors.neutral[600] }}>
                    Your recent searches will appear here.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ pb: 1 }}>
                  {recent.map((r, i) => (
                    <Row key={r}
                      selected={sel === i}
                      onMouseEnter={() => setSel(i)}
                      onClick={() => onPick({ kind: "recent", q: r })}
                      icon={<HistoryIcon sx={{ fontSize: 16, color: colors.neutral[500] }} />}
                      title={r}
                      badge={null}
                    />
                  ))}
                </Box>
              )}

              <SectionLabel>Suggestions</SectionLabel>
              <Box sx={{ pb: 1.5 }}>
                {SUGGESTIONS.map((s, i) => {
                  const idx = recent.length + i;
                  const Icon = s.Icon;
                  return (
                    <Row key={s.id}
                      selected={sel === idx}
                      onMouseEnter={() => setSel(idx)}
                      onClick={() => onPick({ kind: "suggest", payload: s })}
                      icon={<Icon sx={{ fontSize: 16, color: colors.neutral[700] }} />}
                      title={s.title}
                      badge={s.badge}
                    />
                  );
                })}
              </Box>
            </>
          )}

          {/* Below-min state */}
          {q.trim().length > 0 && q.trim().length < MIN_Q_LEN && (
            <Box sx={{ px: 2.5, py: 3, textAlign: "center" }}>
              <Typography sx={{ fontSize: 13, color: colors.neutral[600] }}>
                Keep typing — search needs at least {MIN_Q_LEN} characters.
              </Typography>
            </Box>
          )}

          {/* Error */}
          {err && (
            <Box sx={{ px: 2.5, py: 2.5 }}>
              <Typography sx={{ fontSize: 13, color: colors.red[100] }}>{err}</Typography>
            </Box>
          )}

          {/* Loading state — replaces results until first payload arrives */}
          {q.trim().length >= MIN_Q_LEN && loading && !payload && !err && (
            <Box sx={{ px: 2.5, py: 4, textAlign: "center" }}>
              <CircularProgress size={20} sx={{ color: brandBlue }} />
              <Typography sx={{ mt: 1.25, fontSize: 12, color: colors.neutral[600] }}>
                Searching all sources…
              </Typography>
            </Box>
          )}

          {/* Results — grouped by category, but each row uses the same flat
              icon+title+badge layout so the rhythm matches RECENT/SUGGEST */}
          {typedCategories && !loading && !err && (
            <>
              {typedCategories.length === 0 ? (
                <Box sx={{ px: 2.5, py: 4, textAlign: "center" }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 500, color: colors.neutral[1000], mb: 0.5 }}>
                    No matches
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: colors.neutral[600] }}>
                    Try a hostname, IP, rule name, or URI path.
                  </Typography>
                </Box>
              ) : typedCategories.map((cat) => {
                const meta = CAT_META[cat.id] || { Icon: PageIcon, label: cat.label };
                const Icon = meta.Icon;
                const sectionStart = flatIdxCursor;
                flatIdxCursor += cat.items.length;
                return (
                  <Box key={cat.id}>
                    <SectionLabel right={cat.time_window ? "last " + cat.time_window : null}>
                      {cat.label} · <Box component="span" sx={{ color: colors.neutral[500], fontWeight: 400 }}>{cat.total}{cat.more ? "+" : ""}</Box>
                    </SectionLabel>
                    <Box sx={{ pb: 1 }}>
                      {cat.items.map((it, i) => {
                        const idx = sectionStart + i;
                        return (
                          <Row key={it.id || idx}
                            selected={sel === idx}
                            onMouseEnter={() => setSel(idx)}
                            onClick={() => onPick({ kind: "item", catId: cat.id, item: it })}
                            icon={<Icon sx={{ fontSize: 16, color: colors.neutral[700] }} />}
                            title={highlight(it.title, q)}
                            subtitle={it.subtitle ? highlight(it.subtitle, q) : null}
                            badge={meta.label}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                );
              })}
            </>
          )}
        </Box>

        {/* Footer — quick keyboard hints */}
        <Stack direction="row" alignItems="center" spacing={2}
          sx={{
            flexShrink: 0,
            px: 2.5, py: 1.25,
            borderTop: "1px solid " + colors.neutral[200],
            backgroundColor: colors.neutral[50],
          }}>
          <KeyHint label="↑↓" desc="navigate" />
          <KeyHint label="↵"  desc="open" />
          <KeyHint label="esc" desc="close" />
          <Box sx={{ flex: 1 }} />
          {payload?.took_ms != null && (
            <Typography sx={{ fontSize: 10.5, color: colors.neutral[500], fontFamily: "monospace" }}>
              {payload.took_ms} ms
            </Typography>
          )}
        </Stack>
      </Box>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Small primitives — Row, SectionLabel, KeyHint
// ─────────────────────────────────────────────────────────────────────
function Row({ selected, onMouseEnter, onClick, icon, title, subtitle, badge }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.5}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      sx={{
        mx: 1.25, my: 0.25,
        px: 1.5, py: 1, borderRadius: "8px",
        cursor: "pointer",
        backgroundColor: selected ? "#F0F2FA" : "transparent",
        transition: "background-color 90ms",
      }}>
      <Box sx={{
        width: 24, height: 24, borderRadius: "50%",
        backgroundColor: colors.neutral[50],
        border: "1px solid " + colors.neutral[200],
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 13.5,
            color: colors.neutral[1000],
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{
            fontSize: 11.5, color: colors.neutral[600], mt: 0.125,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>{subtitle}</Typography>
        )}
      </Box>
      {badge && (
        <Typography sx={{ fontSize: 11.5, color: colors.neutral[600], flexShrink: 0 }}>{badge}</Typography>
      )}
    </Stack>
  );
}

function SectionLabel({ children, right }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between"
      sx={{ px: 2.5, pt: 1.5, pb: 0.75 }}>
      <Typography
        sx={{
          fontSize: 10.5,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: colors.neutral[600],
        }}
      >
        {children}
      </Typography>
      {right && (
        <Typography sx={{
          fontSize: 10.5, color: colors.neutral[500], fontFamily: "monospace",
        }}>{right}</Typography>
      )}
    </Stack>
  );
}

function KeyHint({ label, desc }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Box sx={{
        fontFamily: "monospace", fontSize: 10.5,
        border: "1px solid " + colors.neutral[300], borderRadius: "3px",
        padding: "0 5px", color: colors.neutral[700],
        backgroundColor: "#fff",
      }}>{label}</Box>
      <Typography sx={{ fontSize: 11, color: colors.neutral[600] }}>{desc}</Typography>
    </Stack>
  );
}
